import { HTTPError } from 'ky';
import { tempoApi } from '@/lib/api/tempoApi';
import type { Plan, PlanCreateRequest, PlanUpdateRequest } from '@/lib/api/types/adaptivePlanner';
import type {
  PlanAlert,
  PlanSnapshot,
  SyncWithPortfolioResponse,
} from '../types/adaptive-planner/plan';

export const getPlans = async ({ limit }: { limit?: number } = {}): Promise<Plan[]> => {
  const searchParams = new URLSearchParams();

  if (limit !== undefined) {
    searchParams.set('limit', limit.toString());
  }

  const url = `api/v1/adaptive-planner/plans${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

  const response = await tempoApi().get(url).json<Plan[]>();
  return response;
};

const getPlan = async ({ planId }: { planId: number }): Promise<Plan> => {
  const response = await tempoApi().get(`api/v1/adaptive-planner/plans/${planId}`).json<Plan>();
  return response;
};

const createPlan = async ({ data }: { data: PlanCreateRequest }): Promise<Plan> => {
  const response = await tempoApi()
    .post('api/v1/adaptive-planner/plans', {
      json: data,
    })
    .json<Plan>();
  return response;
};

const updatePlan = async ({
  planId,
  data,
}: {
  planId: number;
  data: PlanUpdateRequest;
}): Promise<Plan> => {
  const response = await tempoApi()
    .patch(`api/v1/adaptive-planner/plans/${planId}`, { json: data })
    .json<Plan>();
  return response;
};

const deletePlan = async ({ planId }: { planId: number }): Promise<void> => {
  await tempoApi().delete(`api/v1/adaptive-planner/plans/${planId}`);
};

const syncWithPortfolio = async ({
  planId,
}: {
  planId: number;
}): Promise<SyncWithPortfolioResponse> => {
  return await tempoApi()
    .post(`api/v1/adaptive-planner/plans/${planId}/portfolio-sync`)
    .json<SyncWithPortfolioResponse>();
};

const getAlerts = async ({
  planId,
  alertTypes,
}: {
  planId: number;
  alertTypes?: PlanAlert['alertType'][];
}): Promise<PlanAlert[]> => {
  const searchParams = new URLSearchParams();

  if (alertTypes && alertTypes.length > 0) {
    alertTypes.forEach((type) => {
      searchParams.append('alertTypes', type);
    });
  }

  const url = `api/v1/adaptive-planner/plans/${planId}/alerts${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

  return await tempoApi().get(url).json<PlanAlert[]>();
};

const getLatestSnapshot = async ({
  planId,
  includeMetrics = false,
}: {
  planId: number;
  includeMetrics?: boolean;
}): Promise<PlanSnapshot | null> => {
  const searchParams = new URLSearchParams();
  if (includeMetrics) {
    searchParams.set('includeMetrics', 'true');
  }

  const url = `api/v1/adaptive-planner/plans/${planId}/snapshots/latest${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

  try {
    return await tempoApi().get(url).json<PlanSnapshot>();
  } catch (err) {
    if (err instanceof HTTPError && err.response.status === 404) {
      return null;
    }
    throw err;
  }
};

export const getPlanByLoopId = async ({ loopId }: { loopId: number }): Promise<Plan | null> => {
  try {
    return await tempoApi().get(`api/v1/adaptive-planner/plans/by-loop-id/${loopId}`).json<Plan>();
  } catch (err) {
    if (err instanceof HTTPError && err.response.status === 404) {
      return null;
    }
    throw err;
  }
};

export const AdaptivePlannerPlan = {
  getPlan,
  getPlanByLoopId,
  getPlans,
  updatePlan,
  deletePlan,
  createPlan,
  syncWithPortfolio,
  getAlerts,
  getLatestSnapshot,
} as const;
